import React from 'react';

import { isDefined, isEmpty } from '@nfctron/nexus-core';
import { UiButton, UiDrawer, UiDrawerContent, UiDrawerFooter, UiDrawerHeader, UiDrawerTitle, useFormat } from '@nfctron/nexus-react';

import { Text } from '@mantine/core';
import { CheckIcon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { Form } from '@root/features/form/components/Form/Form';
import { type FormProps } from '@root/features/form/components/Form/Form.types';

import { Gallery } from '@root/lib/components/Gallery/Gallery';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { type CartStore, type CartStoreCartedTicketsSlice, useCartStore, useCartStoreExtraProductsProvider } from '../../stores/cartStore';
import { ProductConfigurationForm } from '../ProductConfigurationForm';

/**
 * Event/Cart/ConfigSheetProductAtom atom
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ConfigSheetProductAtom: AtomComponent<ConfigSheetProductAtomProps> = ({ open, dismiss, input }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ConfigSheetProductAtom.displayName);
	const format = useFormat();
	const extraProducts = useCartStoreExtraProductsProvider();

	/** get cartStore data */
	const { event, cartedTicket, ticketType } = useCartStore((state) => {
		const { ticket, ticketType } = state.getCartedTicket({ cartTicketId: input.cartTicketId });
		return {
			event: { currency: { baseCurrency: { currencyId: state.cartConfig.currencyId } } },
			cartedTicket: ticket,
			ticketType,
		};
	});

	/** get initial product values */
	const { initialProductConfig, initialProductValues } = useCartStore((state) => {
		const productConfig = state.getExtraProductById({ ticketProductId: input.ticketProductId });
		return {
			initialProductConfig: productConfig,
			initialProductValues: cartedTicket.extraProducts.find((ep) => ep.cartProductId === input.cartProductId) ?? {
				cartProductId: uuidv4(),
				ticketProductId: productConfig.ticketProductId,
				count: 1,
				optionalText: '',
				chips: state.getExtraProductInitialChips({
					ticketProductId: productConfig.ticketProductId,
					ticketTypeId: cartedTicket.ticketTypeId,
				}),
			},
		};
	});

	/** product form */
	const productForm = useForm<CartStoreCartedTicketsSlice.CartedTicketExtraProduct>({
		mode: 'all',
		criteriaMode: 'firstError',
		shouldFocusError: true,
		reValidateMode: 'onChange',
		defaultValues: initialProductValues,
	});

	/** watch form values */
	const values = useWatch<CartStoreCartedTicketsSlice.CartedTicketExtraProduct>({
		control: productForm.control,
	});

	/** get current product state */
	const current = useCartStore((state) => {
		/** ticketProductId within values must be defined */
		if (isEmpty(values.ticketProductId)) throw new Error(`[${ConfigSheetProductAtom.displayName}]: ticketProductId cannot be empty`);

		/** get current extra product config */
		const extraProductConfig = state.getExtraProductById({ ticketProductId: values.ticketProductId });

		/** get current extra product meta */
		const extraProductState = state.getExtraProductStateById({
			ticketProductId: extraProductConfig.ticketProductId,
			cartTicketId: input.cartTicketId,
			cartProductId: input.cartProductId,
		});

		return {
			config: extraProductConfig,
			meta: extraProductState,
			price: isEmpty(values.cartProductId)
				? 0
				: state.getCartedExtraProductPrice({
						cartProductId: values.cartProductId,
						cartTicketId: input.cartTicketId,
						ticketProductId: values.ticketProductId,
					}),
		};
	});

	/**
	 * Handler for submitting the form
	 * @type {(data: UseCartTicketsTicketExtraProduct, event?: React.BaseSyntheticEvent) => any}
	 */
	const onSubmit = React.useCallback<FormProps<CartStoreCartedTicketsSlice.CartedTicketExtraProduct>['onSubmit']>(
		(fields) => {
			if (!isEmpty(input.cartProductId)) {
				/** update existing extra product */
				extraProducts.cartOps.update({
					cartTicketId: input.cartTicketId,
					cartProductId: input.cartProductId,
					update: fields,
				});
			} else {
				/** if already carted, update current product instance */
				const alreadyCarted = isEmpty(current.config.optionalTextRegex)
					? cartedTicket.extraProducts.find((ep) => ep.ticketProductId === fields.ticketProductId)
					: null;
				if (isDefined(alreadyCarted)) {
					extraProducts.cartOps.update({
						cartProductId: alreadyCarted.cartProductId,
						cartTicketId: input.cartTicketId,
						update: { count: alreadyCarted.count + 1 },
					});
				} else {
					extraProducts.cartOps.add({
						ticketProductId: fields.ticketProductId,
						cartTicketId: input.cartTicketId,
						initialValue: fields,
					});
				}
			}

			/** close the sheet */
			dismiss();
		},
		[current.config.optionalTextRegex, dismiss, extraProducts.cartOps, input.cartProductId, input.cartTicketId, cartedTicket.extraProducts],
	);

	return (
		<UiDrawer open={open} onClose={dismiss}>
			<UiDrawerContent className="mx-auto max-w-screen-sm">
				<UiDrawerHeader className="flex justify-center border-b border-zinc-200 py-6">
					<UiDrawerTitle asChild>
						<Text fw={700} c="lucky-point" fz={18}>
							{t('header')}
						</Text>
					</UiDrawerTitle>
				</UiDrawerHeader>

				<Form withInvisibleSubmit form={productForm} onSubmit={onSubmit} className="flex max-h-full flex-col gap-4 overflow-scroll p-4">
					{/* gallery */}
					{isDefined(current.config.gallery) && <Gallery gallery={current.config.gallery} />}
					{/* info section */}
					<section>
						{/* product name */}
						<Text c="lucky-point" size="xl">
							{current.config.name}
						</Text>
						{/* description */}
						{!isEmpty(initialProductConfig.description) && (
							<Text size="md" c="dimmed">
								{current.config.description}
							</Text>
						)}
					</section>
					{/* product configuration form */}
					{isDefined(current) && <ProductConfigurationForm extraProductConfig={current.config} ticketTypeConfig={ticketType} form={productForm} />}
				</Form>

				<UiDrawerFooter className="flex items-center gap-3">
					{current.meta.soldOut ? (
						<UiButton type="submit" variant="primary" fullWidth disabled>
							{t('soldOutButton.text')}
						</UiButton>
					) : current.meta.quantityLimited ? (
						<UiButton type="submit" variant="primary" fullWidth disabled>
							{t('quantityLimitedButton.text')}
						</UiButton>
					) : (
						<UiButton
							type="submit"
							onClick={productForm.handleSubmit(onSubmit)}
							variant="primary"
							fullWidth
							leftSection={<CheckIcon />}
							disabled={!productForm.formState.isValid}
						>
							{!isEmpty(input.cartProductId)
								? current.price === 0
									? t('updateButtonFree.text')
									: t('updateButton.text', {
											price: format.price(current.price, event.currency.baseCurrency.currencyId),
										})
								: current.price === 0
									? t('createButtonFree.text')
									: t('createButton.text', {
											price: format.price(current.price, event.currency.baseCurrency.currencyId),
										})}
						</UiButton>
					)}
				</UiDrawerFooter>
			</UiDrawerContent>
		</UiDrawer>
	);
};

/**
 * Event/Cart/ProductSimpleConfigSheet atom props type
 * @export
 */
export type ConfigSheetProductAtomProps = {
	open: boolean;
	dismiss: () => void;
	input: NonNullable<CartStore.Store['extraProductConfigSheetProductInput']>;
};

/**
 * Event/Cart/ConfigSheetProductAtom atom display name
 * @type {ComponentDisplayName<"atom">>}
 */
ConfigSheetProductAtom.displayName = 'Event/Cart/ConfigSheetProductAtom';
